import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { userService } from '../firebase/services';
import { format } from 'date-fns';

const ParkingUserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'created',
    direction: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [sendingReferrals, setSendingReferrals] = useState(false);
  const [bulkSelectionCount, setBulkSelectionCount] = useState(50);

  // Load users from Firebase
  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await userService.getAllUsers();
      setUsers(usersData);
      setFilteredUsers(usersData);
      setError(null);
      // Clear selections when users are reloaded
      setSelectedUsers(new Set());
    } catch (err) {
      setError('Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    
    // Cleanup function to clear selections when component unmounts
    return () => {
      setSelectedUsers(new Set());
    };
  }, []);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
      setCurrentPage(1);
    } else {
      const filtered = users.filter(user => 
        user.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm) ||
        user.city?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
      setCurrentPage(1);
    }
    // Clear selections when search changes
    setSelectedUsers(new Set());
  }, [searchTerm, users]);

  // Handle user selection
  const handleUserSelection = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  // Handle select all users
  const handleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      const allUserIds = filteredUsers.map(user => user.id);
      setSelectedUsers(new Set(allUserIds));
    }
  };

  // Handle bulk selection
  const handleBulkSelection = (count) => {
    const availableUsers = filteredUsers.slice(0, count);
    const userIds = availableUsers.map(user => user.id);
    setSelectedUsers(new Set(userIds));
  };

  // Handle select first N users
  const handleSelectFirstN = () => {
    const count = parseInt(bulkSelectionCount);
    if (count > 0) {
      handleBulkSelection(count);
    }
  };

  // Handle clear selection
  const handleClearSelection = () => {
    setSelectedUsers(new Set());
  };

  // Handle select all users on current page
  const handleSelectAllOnCurrentPage = () => {
    const currentPageUserIds = currentUsers.map(user => user.id);
    const allSelected = new Set(selectedUsers);
    
    // Check if all current page users are already selected
    const allCurrentPageSelected = currentPageUserIds.every(id => allSelected.has(id));
    
    if (allCurrentPageSelected) {
      // If all current page users are selected, deselect them
      currentPageUserIds.forEach(id => allSelected.delete(id));
    } else {
      // If not all current page users are selected, select them all
      currentPageUserIds.forEach(id => allSelected.add(id));
    }
    
    setSelectedUsers(allSelected);
  };

  // Helper function to get selection info
  const getSelectionInfo = () => {
    const selectedOnCurrentPage = currentUsers.filter(user => selectedUsers.has(user.id)).length;
    return {
      totalSelected: selectedUsers.size,
      selectedOnCurrentPage,
      totalOnCurrentPage: currentUsers.length
    };
  };

    // Handle sending WhatsApp referrals
  const handleSendWhatsAppReferrals = async () => {
    if (selectedUsers.size === 0) {
      alert('Please select at least one user to send WhatsApp referrals.');
      return;
    }

    // Removed 900 limit - users can now select any number of users

    try {
      console.log('Starting handleSendWhatsAppReferrals');
      console.log('Selected users:', Array.from(selectedUsers));
      setSendingReferrals(true);
      
      // Update askReferral field for each selected user using local service
      const selectedUsersArray = Array.from(selectedUsers);
      const results = [];
      
      for (const userId of selectedUsersArray) {
        try {
          await userService.updateUserAskReferral(userId);
          results.push({ userId, success: true });
        } catch (error) {
          console.error(`Error updating user ${userId}:`, error);
          results.push({ userId, success: false, error: error.message });
        }
      }
      
      const successfulUpdates = results.filter(r => r.success).length;
      const failedUpdates = results.filter(r => !r.success).length;
      
      console.log('Update results:', results);
      
      // Clear selections after successful update
      setSelectedUsers(new Set());
      
      // Reload users to reflect changes
      await loadUsers();
      
      if (failedUpdates === 0) {
                 alert(`Successfully updated askReferral field for ${successfulUpdates} user(s). The referral status will show as "Processing..." for the next few minutes while your Cloud Function processes the requests. You can see the request date in the new "Last Referral Request" column.`);
      } else {
        alert(`Updated askReferral field for ${successfulUpdates} user(s). ${failedUpdates} updates failed. Check console for details.`);
      }
    } catch (error) {
      console.error('Error sending WhatsApp referrals:', error);
      alert('Failed to update askReferral fields. Please try again.');
    } finally {
      setSendingReferrals(false);
    }
  };

  // Helper function to check if user was recently updated
  const isRecentlyUpdated = (user) => {
    if (!user.referralRequestedAt) return false;
    
    const updateTime = user.referralRequestedAt.toDate ? user.referralRequestedAt.toDate() : new Date(user.referralRequestedAt);
    const now = new Date();
    const timeDiff = now - updateTime;
    
    // Consider "recent" if updated within last 5 minutes
    return timeDiff < 5 * 60 * 1000;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUpIcon className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUpIcon className="w-4 h-4 text-blue-600" />
      : <ChevronDownIcon className="w-4 h-4 text-blue-600" />;
  };

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];
    
    // Handle timestamp conversion for date sorting
    if (sortConfig.key === 'created') {
      aValue = aValue?.toDate ? aValue.toDate() : new Date(aValue);
      bValue = bValue?.toDate ? bValue.toDate() : new Date(bValue);
    }
    
    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';
    
    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Pagination
  const indexOfLastUser = currentPage * rowsPerPage;
  const indexOfFirstUser = indexOfLastUser - rowsPerPage;
  const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(sortedUsers.length / rowsPerPage);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return 'N/A';
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Keep selections when navigating between pages
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
    // Clear selections when rows per page changes
    setSelectedUsers(new Set());
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      // Keep selections when navigating between pages
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      // Keep selections when navigating between pages
    }
  };

  // Calculate display range
  const startRange = indexOfFirstUser + 1;
  const endRange = Math.min(indexOfLastUser, sortedUsers.length);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-semibold text-gray-900">User List</h1>
            </div>
            <div className="text-sm text-gray-500">
              {filteredUsers.length} users found
              {selectedUsers.size > 0 && (
                <span className="ml-2 text-blue-600 font-medium">
                  • {selectedUsers.size} selected
                  {(() => {
                    const info = getSelectionInfo();
                    if (info.selectedOnCurrentPage > 0) {
                      return ` (${info.selectedOnCurrentPage} on page)`;
                    }
                    return '';
                  })()}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6 py-6">

        {/* WhatsApp Referral Button */}
        {selectedUsers.size > 0 && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-blue-900">
                  Send WhatsApp Referrals
                </h3>
                <p className="text-sm text-blue-700">
                  {selectedUsers.size} user(s) selected out of {filteredUsers.length} total users
                  {(() => {
                    const info = getSelectionInfo();
                    if (info.selectedOnCurrentPage > 0) {
                      return ` (${info.selectedOnCurrentPage} on current page)`;
                    }
                    return '';
                  })()}
                </p>
                {selectedUsers.size > 1000 && (
                  <p className="text-sm text-orange-600 mt-1">
                    ⚠️ Large selection detected. Processing may take longer for {selectedUsers.size} users.
                  </p>
                )}
              </div>
              <button
                onClick={handleSendWhatsAppReferrals}
                disabled={sendingReferrals}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {sendingReferrals ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Processing {selectedUsers.size} users...</span>
                  </>
                ) : (
                  <>
                    <span>Send Referral Via WhatsApp</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Search and Bulk Selection */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users by name, phone, or city..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedUsers(new Set());
                    }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Bulk Selection Dropdown */}
            <div className="sm:w-64">
              <select
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '50') handleBulkSelection(50);
                  else if (value === '100') handleBulkSelection(100);
                  else if (value === '200') handleBulkSelection(200);
                  else if (value === '500') handleBulkSelection(500);
                  else if (value === '900') handleBulkSelection(900);
                  else if (value === 'all') handleBulkSelection(filteredUsers.length);
                  else if (value === 'page') handleSelectAllOnCurrentPage();
                  // Reset to default after selection
                  e.target.value = '';
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                defaultValue=""
              >
                <option value="" disabled>Select bulk action...</option>
                <option value="50">First 50 users</option>
                <option value="100">First 100 users</option>
                <option value="200">First 200 users</option>
                <option value="500">First 500 users</option>
                <option value="900">First 900 users</option>
                <option value="all">All users ({filteredUsers.length})</option>
                <option value="page">Current page ({currentUsers.length})</option>
              </select>
            </div>

            {/* Clear Selection Button */}
            {selectedUsers.size > 0 && (
              <button
                onClick={handleClearSelection}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Clear Selection
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading users...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {/* Selection column */}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="space-y-2">
                          {/* Select All */}
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                              onChange={handleSelectAll}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-xs text-gray-500">
                              All ({filteredUsers.length})
                            </span>
                          </div>
                          {/* Select All on Current Page */}
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={currentUsers.length > 0 && currentUsers.every(user => selectedUsers.has(user.id))}
                              onChange={handleSelectAllOnCurrentPage}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-xs text-gray-500">
                              Page ({currentUsers.length})
                            </span>
                          </div>
                        </div>
                      </th>
                                             {[
                         { key: 'firstname', label: 'First Name' },
                         { key: 'lastname', label: 'Last Name' },
                         { key: 'phone', label: 'Phone Number' },
                         { key: 'city', label: 'City' },
                         { key: 'state', label: 'State' },
                         { key: 'created', label: 'Membership From Date' },
                         { key: 'vehiclecount', label: 'Number of Vehicles' },
                         { key: 'askReferral', label: 'Referral Status' },
                         { key: 'referralRequestedAt', label: 'Last Referral Request' }
                       ].map((column) => (
                        <th
                          key={column.key}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort(column.key)}
                        >
                          <div className="flex items-center space-x-1">
                            <span>{column.label}</span>
                            {getSortIcon(column.key)}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentUsers.map((user) => (
                      <tr 
                        key={user.id} 
                        className="hover:bg-gray-50"
                      >
                        {/* Selection checkbox */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedUsers.has(user.id)}
                            onChange={() => handleUserSelection(user.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </td>
                        <td 
                          className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 cursor-pointer"
                          onClick={() => navigate(`/userdetail/${user.id}`)}
                        >
                          {user.firstname || 'N/A'}
                        </td>
                        <td 
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                          onClick={() => navigate(`/userdetail/${user.id}`)}
                        >
                          {user.lastname || 'N/A'}
                        </td>
                        <td 
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                          onClick={() => navigate(`/userdetail/${user.id}`)}
                        >
                          {user.phone || 'N/A'}
                        </td>
                        <td 
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                          onClick={() => navigate(`/userdetail/${user.id}`)}
                        >
                          {user.city || 'N/A'}
                        </td>
                        <td 
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                          onClick={() => navigate(`/userdetail/${user.id}`)}
                        >
                          {user.state || 'N/A'}
                        </td>
                        <td 
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                          onClick={() => navigate(`/userdetail/${user.id}`)}
                        >
                          {formatDate(user.created)}
                        </td>
                        <td 
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                          onClick={() => navigate(`/userdetail/${user.id}`)}
                        >
                          {user.vehiclecount || 0}
                        </td>
                                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                           {user.askReferral ? (
                             <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                               <CheckIcon className="w-4 h-4 mr-1" />
                               Referral Sent
                             </span>
                           ) : isRecentlyUpdated(user) ? (
                             <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                               <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                               Processing...
                             </span>
                           ) : (
                             <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                               Pending
                             </span>
                           )}
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                           {user.referralRequestedAt ? formatDate(user.referralRequestedAt) : 'N/A'}
                         </td>
                       </tr>
                     ))}
                   </tbody>
                 </table>
               </div>

              {/* Pagination */}
              {sortedUsers.length > 0 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={handlePrevious}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={handleNext}
                      disabled={currentPage >= totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startRange}</span> to <span className="font-medium">{endRange}</span> of{' '}
                        <span className="font-medium">{sortedUsers.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={handlePrevious}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Previous</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button
                          onClick={handleNext}
                          disabled={currentPage >= totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Next</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default ParkingUserList; 